import React from "react";
import { Empty } from "./OverviewTab";
import { useT } from "../../lib/i18n";

export default function AiActTab({ version }) {
  const t = useT();
  if (!version) return <Empty msg={t("ov.no_analysis")} />;
  const a = version.ai_act || { applicable: false, rationale: "" };
  if (!a.applicable) {
    return (
      <div className="grid-card p-10 text-center">
        <div className="text-sm text-[var(--text-2)]">{t("aiact.not_applicable")}</div>
      </div>
    );
  }
  const legacyHighRisk = a.ai_classification === "high_risk";
  const priorityReview = a.potential_high_risk_review === true || legacyHighRisk;
  const criteria = (a.assessment_criteria || [
    "article_6_1_annex_i",
    "article_6_2_annex_iii",
    "intended_purpose_and_mdr_route",
  ]).map((criterion) => t(`aiact.criteria.${criterion}`));
  return (
    <div className="space-y-4">
      <p className="rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-4 py-3 text-xs leading-relaxed text-[var(--text-2)]" data-testid="aiact-scope-note">
        {t("aiact.scope_note")}
      </p>
      <div className="grid-card p-5 space-y-3" data-testid="aiact-assessment">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("aiact.assessment_basis")}</div>
          <span className="mt-2 inline-flex rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-sm font-bold text-[var(--text)]" data-testid="aiact-class">
            {t("aiact.assessment_required")}
          </span>
        </div>
        {priorityReview && (
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300" data-testid="aiact-priority">
            {legacyHighRisk ? t("aiact.legacy_high_risk") : t("aiact.priority_review")}
          </p>
        )}
        {!legacyHighRisk && (
          <p className="text-sm text-[var(--text-2)]" data-testid="aiact-rationale">
            {priorityReview ? t("aiact.rationale.priority") : t("aiact.rationale.standard")}
          </p>
        )}
        <p className="text-sm leading-relaxed text-[var(--text-2)]">{t("aiact.article6_note")}</p>
      </div>

      <Block title={t("aiact.assessment_basis")} items={criteria} />
      <Block title={t("aiact.review_points")} items={[
        t("aiact.point.transparency"),
        t("aiact.point.oversight"),
        t("aiact.point.data"),
      ]} />
      <div className="flex flex-col gap-2 text-xs sm:flex-row sm:gap-5">
        <a className="font-semibold text-[var(--accent-text)] hover:underline" href="https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02024R1689-20260727" target="_blank" rel="noreferrer">
          {t("aiact.source_law")}
        </a>
        <a className="font-semibold text-[var(--accent-text)] hover:underline" href="https://digital-strategy.ec.europa.eu/en/library/draft-commission-guidelines-classification-high-risk-ai-systems" target="_blank" rel="noreferrer">
          {t("aiact.source_guidance")}
        </a>
      </div>
    </div>
  );
}

function Block({ title, items }) {
  return (
    <div className="grid-card p-5">
      <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)] mb-3">{title}</div>
      <ul className="text-sm space-y-2 list-disc list-inside">
        {items.map((it) => <li key={it}>{it}</li>)}
      </ul>
    </div>
  );
}
